import request, { baseURL } from "@/http"
import http from "@/http/http"
import type { ResultVO, StoreProfile } from "@/api"
// ==============================================
// 筛选条件-选址列表
export interface GetSitesFilters {
  // 分析时间
  analysisTime: string[]
  // 行业
  industryCategories: string[]
  // 状态
  analysisStatus: string[]
  // 仅个人
  isPersonal?: boolean
}

// 筛选条件-点位列表
export interface PointLocationFilters {
  // 推荐星级
  recomendScore: string[]
}

interface SitesPoiArgs {
  // 纬度
  latitude: number
  // 经度
  longitude: number
}
export interface SitePoiVO {
  // 选址POI名称
  poiName: string
  // 选址地址
  address: string
  // 纬度
  latitude: number
  // 经度
  longitude: number
}
/**
 * 获取选址点的POI信息
 */
export async function getSitesPoi({ longitude, latitude }: SitesPoiArgs): Promise<SitePoiVO[]> {
  try {
    const result = await http.get<SitePoiVO[]>(`/siteSelection/sites/poi`, {
      params: {
        longitude,
        latitude,
      },
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

interface GetSitesArg {
  // 城市ID
  cityId: string
  // 品牌ID
  brandId: string
  // 查询页数，不能小于1
  page: number
  // 查询条数，不能小于1
  limit: number
  // 选址方式，格式：[\"1990-01-01\", \"2022-12-31\"]，传空表示不限定时间
  analysisTime?: string[]
  // 选址行业分类，传空表示不限定行业分类
  industryCategories?: string[]
  // 选址分析状态，传空表示不限定分析状态（SiteAnalysisStatus）
  analysisStatus?: string[]
  // 选址方式（SiteChooseWay）
  siteChooseWay?: string
  // 任务ID
  taskId?: string
  // 推荐分数
  recomendScore?: number[][]
  // 点位收藏状态（true - 收藏，false - 未收藏，默认不限）
  collectionStatus?: boolean
}
export interface SiteVO {
  // id
  siteId: string
  // 网格ID
  gridId: string
  // 选址POI名称
  poiName: string
  // 选址地址
  address: string
  // 服务行业
  industryName: string
  // 选址分析状态（SiteAnalysisStatus）
  analysisStatus: string
  // 推荐指数（0-1）
  recommendScore: number
  // 推荐时间/分析时间，格式：yyyy-MM-dd
  analysisTime: string
  // 纬度
  latitude: number
  // 经度
  longitude: number
  // 任务
  taskId: string
  taskName: string
  // 收藏状态
  collectionStatus: boolean
}
interface GetSitesVO {
  // 数据列表
  data: SiteVO[]
  // 总条数
  total: number
  // 总页数
  totalPage: number
}
/**
 * 获取选址列表信息
 */
export async function getSitesList(params: GetSitesArg): Promise<GetSitesVO> {
  try {
    const postData: any = {
      ...params,
      analysisStatus: params.analysisStatus?.[0],
    }
    const result = await request.post<any>("/siteSelection/sites", postData)
    return result?.data?.data
  } catch (error) {
    throw error
  }
}

interface SiteSelectionTagDTO {
  // 标签ID
  id: string
  // 标签key ID
  keyId: string
}
export interface Restriction {
  // 资源标签ID
  category: string
  // 限制半径
  radius: number
  // 限制条件（SiteSelectionRestrictionCondition）
  condition: string
  // 限制值
  value: string
}
export interface SiteDTO {
  // 选址POI名称
  poiName: string
  // 选址地址
  address: string
  // 选址经度
  latitude: number
  // 选址纬度
  longitude: number
}
export interface SiteIndustryDTO {
  // 行业分类
  industryCategory: string
  // 行业名称
  industryName: string
}
export interface CreateSiteTaskArg {
  // 城市ID
  cityId: string
  // 品牌ID
  brandId: string
  // 选址方式（SiteChooseWay）
  siteChooseWay: string
  // 行业
  siteIndustry: SiteIndustryDTO
  /**
   * 专家经验
   */
  // 选址列表
  sites?: SiteDTO[]
  /**
   * 智能推荐
   */
  // 项目名称
  taskName?: string
  // 区县ID列表（默认查询指定城市下的所有区县）
  districtIds?: string[]
  // 目标人群标签
  targetTags?: SiteSelectionTagDTO[]
  // 生态资源标签
  resourceTags?: Restriction[]
  areaCode?: string
  districtId?: string
}
/**
 * 新增选址
 */
export async function createSitesTask(params: CreateSiteTaskArg): Promise<ResultVO> {
  try {
    const result = await request.post<ResultVO>("/siteSelection/sites/tasks", params)
    return result?.data
  } catch (error) {
    throw error
  }
}

interface SiteIndustryArgs {
  // 行业等级，多个等级用逗号分隔
  levels: string
  // 行业名称，支持模糊查询
  name?: string
}
export interface SiteIndustryVO {
  // 行业分类
  industryCategory: string
  // 行业名称
  industryName: string
  // 行业等级
  industryLevel: number
}
/**
 * 根据行业等级获取选址行业列表
 */
export async function getSiteIndustryList({
  levels,
  name,
}: SiteIndustryArgs): Promise<SiteIndustryVO[]> {
  try {
    const result = await request.get<any>(`/siteSelection/sites/industry`, {
      params: {
        name,
        level: levels,
      },
    })
    return result?.data?.data
  } catch (error) {
    throw error
  }
}

interface SitesDetailArgs {
  siteId: string
}
/**
 * 获取详情
 */
export async function getSitesDetail(params: SitesDetailArgs): Promise<StoreProfile> {
  try {
    const result = await request.post<StoreProfile>("/v1/industry/sites/detail", params)
    return result?.data
  } catch (error) {
    throw error
  }
}

/**
 * 点位批量导入模板下载
 */
export const siteImportTemplate = `${baseURL}/v1/industry/sites/model/download`
export async function downloadSiteImportTemplate(): Promise<any> {
  try {
    const result = await request.get<any>("/v1/industry/sites/model/download", {
      headers: {
        responseType: "blob", // arraybuffer
      },
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface SiteImportPoiVO {
  // 选址POI名称
  poiName: string
  // 选址地址
  address: string
  // 选址纬度
  latitude: number
  // 选址经度
  longitude: number
  // 当前点位导入失败原因（当点位导入失败时返回）
  failMessage: string
}
interface SiteImportResultVO {
  // 导入成功点位
  successSites: SiteImportPoiVO[]
  // 导入失败点位
  failureSites: SiteImportPoiVO[]
}
interface SitesBatchVO {
  // 导入结果编码，返回200时为导入成功，当点位全部导入成功时为导入成功，否则为导入失败
  code: number
  // 导入结果话术
  message: string
  // 导入点位信息
  data: SiteImportResultVO
}
/**
 * 点位批量导入
 * @param cityId
 * @param brandId
 * @param siteChooseWay // 选址方式：SiteChooseWay
 * @param file
 */
export async function importSitesBatch(params: any): Promise<SitesBatchVO> {
  try {
    const cityId = params.get("cityId") || ""
    const brandId = params.get("brandId") || ""
    const result = await request.post<SitesBatchVO>(
      `/v1/industry/sites/batchImport/${brandId}/${cityId}`,
      params,
    )
    return result?.data
  } catch (error) {
    throw error
  }
}

export type Analysis = {
  radar: Radar // 能力雷达（四维雷达图）
  population: Population // 人口
  commerce: Commerce // 商业成熟度
  transportation: Transportation // 交通
  consumption: Consumption // 消费水平
}
export type Radar = {
  population: number // 人口
  commerce: number // 商业成熟度
  transportation: number // 交通便利性
  consumption: number // 消费水平
}
export type Population = {
  range: number // 数据范围
  permanentResidence: PopulationInfo // 常驻人口
  worker: PopulationInfo // 工作人口
  residence: PopulationInfo // 居住人口
}
export type PopulationInfo = {
  population: number // 人口
  density: number // 平均密度
  compare: string // 比较结果（高、中、低）
}

export type Commerce = {
  range: number // 数据范围（3km)
  commerceInfo: { type: string; name: string; count: number; unit: string }[]
}

export type Transportation = {
  range: number // 数据范围
  metro: TransportationInfo // 地铁站
  bus: TransportationInfo // 公交站
  parkingGarage: TransportationInfo // 停车场
}

export type TransportationInfo = {
  count: number // 数量
  distance: number // 最近距离
  walkTime: number // 步行时间
  bicycleTime: number // 骑行时间
}

export type Residence = {
  luxury: number // 豪宅社区
  villa: number // 别墅
  highEnd: number // 高端社区
  normal: number // 普通社区
}

type ProfileBasicItem = {
  id: string
  name: string
  ratio: number
}

export type Consumption = {
  range: number // 数据范围
  // high: number // 高占比
  // middle: number // 中占比
  // low: number // 低占比
  // residence: Residence // 住宅等级占比
  consumptionRatio: ProfileBasicItem[] // 消费占比
  residenceRatio: ProfileBasicItem[] // 住宅等级占比
}

export type Competition = {
  compCount: number // 1公里范围同业门店数
  farCompCount: number // 超1公里范围同业门店数
  wholeCompCount: number // 全市同业门店数
  compStores: CompStore[] // 同业品牌列表
  compBrands: CompBrand[] // 同业topN品牌列表
  wholeCompBrands: CompBrand[] // 全市top5品牌
  wholeCompStores: { longitude: number; latitude: number }[]
}

export type CompStore = {
  poiName: string // 同业品牌门店
  distance: number // 距离
}
export type CompBrand = {
  brandName: string // 品牌名称
  count: number // 品牌数量
}

export type SiteSelectionReport = {
  // 选址ID
  siteId: string
  // 地址
  address: string
  // 选址点位名称
  poiName: string
  // 报告发布时间
  analysisTime: number
  // 评分
  recommendScore: number
  // 立地分析
  analysis: Analysis
  // 竞争生态
  competition: Competition
  // 人口结构
  population: any
}

/**
 * 选址报告详情
 *
 * @export
 * @param {string} siteId
 * @return {*}
 */
export async function getSiteSelectionReport(siteId: string) {
  try {
    const result: any = await http.get<SiteSelectionReport>(`/siteSelection/sites/report`, {
      params: { siteId },
    })
    return result.data
  } catch (error) {
    throw error
  }
}

/**
 * 星巴克-选址报告详情
 */
export async function getStarbucksReport(siteId: string) {
  try {
    const result = await request.get<SiteSelectionReport>(`/v2/starbucks/report`, {
      params: { siteId },
    })
    return result.data
  } catch (error) {
    throw error
  }
}

export interface SiteSelectionTagVO {
  // 标签key ID
  keyId: string
  // 标签ID
  id: string
  // 标签名称
  label: string
  // 子标签
  children?: SiteSelectionTagVO[]
}

/**
 * 目标人群标签查询
 */
export async function getTagetTags(): Promise<SiteSelectionTagVO[]> {
  try {
    const result: any = await request.get<SiteSelectionTagVO[]>("/siteSelection/sites/targetTag")
    return result?.data?.data
  } catch (error) {
    throw error
  }
}

/**
 * 生态资源标签查询
 */
export async function getResourceTags(): Promise<SiteSelectionTagVO[]> {
  try {
    const result: any = await request.get<SiteSelectionTagVO[]>("/siteSelection/sites/resourceTag")
    return result?.data?.data
  } catch (error) {
    throw error
  }
}

interface SiteCollectArg {
  // 任务Id
  taskId: string
  // 点位Id
  siteId: string
}

/**
 * 智能推荐点位收藏
 */
export async function collectRecommendSites(params: SiteCollectArg): Promise<ResultVO> {
  try {
    const result = await request.put<ResultVO>("/v1/industry/sites/recommend/collect", params)
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface SiteRecommendTask {
  // 任务ID（项目ID）
  taskId: string
  // 推荐时间/分析时间
  analysisTime: string
  // 点位数量
  siteQuantity: number
  // 分析状态
  analysisStatus: string
}

interface SiteSelectionTaskVO {
  // 数据列表
  data: SiteRecommendTask[]
  // 总条数
  total: number
  // 总页数
  totalPage: number
}

interface GetTasksArg {
  // 城市ID
  cityId: string
  // 品牌ID
  brandId: string
  // 选址方式
  analysisTime: string[]
  // 选址行业分类
  industryCategories: string[]
  // 选址分析状态（SiteAnalysisStatus）
  analysisStatus: string[]
  // 页数
  page: number
  // 每页查询条数
  limit: number
  // 选址方式（SiteChooseWay）
  siteChooseWay: string
}

/**
 * 智能推荐任务列表查询
 */
export async function getRecommendTasks(params: GetTasksArg): Promise<SiteSelectionTaskVO> {
  const postData: any = {
    ...params,
    analysisStatus: params.analysisStatus?.[0],
  }
  try {
    const result = await request.post<SiteSelectionTaskVO>(
      "/siteSelection/sites/siteTask",
      postData,
    )
    return result?.data
  } catch (error) {
    throw error
  }
}

interface StarbucksScoreArgs {
  name: string
  cityId: string
  poiId: string
  lon: number
  lat: number
  // Grid范围内商户的数量
  storeCount: number
  // Top9业态占比数值（表示百分比）（0-100）
  topNine: number
  // Grid范围内所有商户的人均消费
  averageConsumption: number
  // Grid范围内白领职业分段占比（0-1）
  occupationRatio: number
  // 工作人数指数（0-10）
  // workingIndex: number
  // 工作人口
  workingPopulation: number
}

/**
 * 获取选址评分
 */
export async function getStarbucksScore(params: StarbucksScoreArgs): Promise<any[]> {
  try {
    const result = await request.post<any[]>(`/v2/starbucks/score`, params)
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface StarbucksSiteVO {
  name: string
  createdAt: string
  lat: number
  lon: number
  score: number
  taskId: string
}

interface StarbucksListArgs {
  // 城市
  cityId: string
  // 分析时间
  startDate?: string
  endDate?: string
}

/**
 * 自选推荐-获取选址列表
 */
export async function getStarbucksList(params: StarbucksListArgs): Promise<StarbucksSiteVO[]> {
  try {
    const result = await request.get<StarbucksSiteVO[]>(`/v2/starbucks/list`, {
      params: params,
    })
    return result?.data
  } catch (error) {
    throw error
  }
}
